import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react'
import { createPortal } from 'react-dom'
import { FiHelpCircle } from 'react-icons/fi'

import * as S from './styles'

const Tooltip = ({
  title,
  text,
  icon,
  children,
  noMargin = false,
  shouldShow = true,
  cursor,
  interactiveTrigger = false,
  ariaLabel,
  onTriggerClick,
  onTriggerKeyDown
}: PropsTooltip) => {
  const tooltipId = useId()
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({})
  const [yPosition, setYPosition] = useState<TooltipYPosition>(null)
  const [xPosition, setXPosition] = useState<TooltipXPosition>(null)
  const [isVisible, setIsVisible] = useState(false)

  const targetRef = useRef(null)
  const tooltipRef = useRef(null)

  const shouldShowTooltip = useMemo(
    () => isVisible && shouldShow,
    [isVisible, shouldShow]
  )

  const calculateTooltipPosition = useCallback(() => {
    if (!targetRef.current || !tooltipRef.current) return

    const targetElement = targetRef.current
    const tooltipElement = tooltipRef.current

    const target = targetElement.getBoundingClientRect()
    const tooltip = tooltipElement.getBoundingClientRect()

    const windowWidth = window.innerWidth

    // Define available positions
    const fitsTop = target.top >= tooltip.height
    const fitsLeft = target.left >= tooltip.width
    const fitsRight = target.right + tooltip.width <= windowWidth

    // Handle position Y
    const positionY: TooltipYPosition = fitsTop ? 'top' : 'bottom'
    const isTop = positionY === 'top'

    // Handle position X
    const checkPositonX = () => {
      if (fitsLeft && fitsRight) return 'center'
      if (fitsLeft) return 'left'
      return 'right'
    }
    const positionX: TooltipXPosition = checkPositonX()

    const calcLeftPosition = () => {
      if (fitsLeft && fitsRight)
        return target.left + target.width / 2 - tooltip.width / 2
      if (fitsRight) return target.left - 4
      return target.left + target.width - tooltip.width + 4
    }

    // Define tooltip style
    const style: React.CSSProperties = {
      position: 'fixed',
      zIndex: 9999,
      top: `${isTop ? target.top - tooltip.height : target.bottom}px`,
      left: calcLeftPosition()
    }

    setYPosition(positionY)
    setXPosition(positionX)

    setTooltipStyle(style)
  }, [])

  const showTooltip = useCallback(() => {
    calculateTooltipPosition()
    setIsVisible(true)
  }, [calculateTooltipPosition])

  const hideTooltip = useCallback(() => {
    setIsVisible(false)
  }, [])

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      onTriggerClick?.(event)
      showTooltip()
    },
    [onTriggerClick, showTooltip]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      onTriggerKeyDown?.(event)

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        showTooltip()
      }

      if (event.key === 'Escape') {
        hideTooltip()
      }
    },
    [hideTooltip, onTriggerKeyDown, showTooltip]
  )

  useEffect(() => {
    if (!shouldShowTooltip) return
    calculateTooltipPosition()
    window.addEventListener('resize', calculateTooltipPosition)

    return () => window.removeEventListener('resize', calculateTooltipPosition)
  }, [shouldShowTooltip, calculateTooltipPosition])

  // No interactiveTrigger quem recebe foco é o filho (ou o ícone, quando não
  // há filho), não o wrapper — a descrição precisa ir nele (ver
  // aria-describedby do S.TooltipTrigger). Faz merge com um
  // aria-describedby que o elemento já tivesse, em vez de substituir.
  const withDescribedBy = (element: ReactNode) => {
    if (!interactiveTrigger || !shouldShowTooltip || !isValidElement(element))
      return element

    const existing = (element.props as { 'aria-describedby'?: string })[
      'aria-describedby'
    ]

    return cloneElement(element, {
      'aria-describedby': existing ? `${existing} ${tooltipId}` : tooltipId
    } as Record<string, unknown>)
  }

  const childrenWithDescribedBy = withDescribedBy(children)
  const iconWithDescribedBy = withDescribedBy(icon)

  return (
    <>
      <S.TooltipContainer
        className={`Tooltip__Container d-inline-flex ${noMargin ? 'm-0' : 'ms-1'}`}
      >
        <S.TooltipTrigger
          as={interactiveTrigger ? 'span' : undefined}
          className="Tooltip__Icon d-flex"
          type={interactiveTrigger ? undefined : 'button'}
          onClick={interactiveTrigger ? undefined : handleClick}
          onFocus={showTooltip}
          onKeyDown={interactiveTrigger ? undefined : handleKeyDown}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          onBlur={hideTooltip}
          onTouchStart={showTooltip}
          aria-label={ariaLabel}
          // No modo padrão o próprio trigger recebe foco, então a descrição
          // vai nele. No interactiveTrigger quem recebe foco é o filho (ver
          // abaixo) — não faz sentido duplicar aqui.
          aria-describedby={
            !interactiveTrigger && shouldShowTooltip ? tooltipId : undefined
          }
          ref={targetRef}
        >
          {children ? (
            <S.ChildrenText>{childrenWithDescribedBy}</S.ChildrenText>
          ) : (
            iconWithDescribedBy || <FiHelpCircle className="text-primary" />
          )}
        </S.TooltipTrigger>
      </S.TooltipContainer>

      {shouldShowTooltip &&
        createPortal(
          <S.TooltipContent
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            className="Tooltip__Content"
            data-position-y={yPosition}
            data-position-x={xPosition}
            data-target-width={targetRef.current?.offsetWidth}
            style={{ ...tooltipStyle, cursor }}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
          >
            <div className="arrow" />
            {(title || text) && (
              <div className="text">
                {title && <div className="title">{title}</div>}
                {text}
              </div>
            )}
          </S.TooltipContent>,
          document.body
        )}
    </>
  )
}

export default Tooltip
